// components
import TableView from 'src/components/dashboard/tables/TableView';
import { MBadge } from 'src/components/@material-extend';
// hooks
import { createContext, useContext, useMemo, useState } from 'react';
import useRescues from 'src/hooks/useRescues';

//
import { withPagination } from 'src/utils/pagination';
import moment from 'moment-timezone';
import { formatDisplayDate } from 'careit';
import useAuth from 'src/hooks/useAuth';
import useEventBus from 'src/hooks/useEventBus';
import useDateFilterForTablePersist from 'src/hooks/useDateFilterForTablePersist';
import { useTranslation } from 'react-i18next';
import { useWeightsAndTempsColumns } from 'src/hooks/useWeightsAndTempsColumns';
import CreateDonationButton from 'src/components/CreateDonationButton';
import useDonationStatus from 'src/hooks/useDonationStatus';
import { parseUTC } from 'src/utils/timezones';
import { renderWeekdayDate } from 'src/utils/formatTime';
import { Typography, Tooltip, Box } from '@mui/material';
import useOpenRowDataGrid from 'src/hooks/useOpenRowDataGrid';
import handleDonationRedirection from 'src/utils/handleDonationRedirection';
import { PATH_DASHBOARD } from 'src/routes/paths';

const useIncomingRequestsColumns = () => {
  const { t, i18n } = useTranslation('translation');
  const weightsAndTemps = useWeightsAndTempsColumns();
  const { getStatus } = useDonationStatus();
  const makeOpenRowButton = useOpenRowDataGrid();
  return useMemo(
    () => [
      makeOpenRowButton((params) => handleDonationRedirection(params.row)),
      {
        field: 'rescue_status',
        type: 'string',
        headerName: t('donations.rescue-status'),
        valueGetter: (_value, row) => getStatus(row.rescue_status),
        width: 150,
      },
      {
        field: 'start_date',
        type: 'date',
        headerName: t('donations.start'),
        width: 150,
        valueGetter: (_value, row) =>
          parseUTC(row.start_date, { timezone_id: row.donor_timezone_id }),
        renderCell: renderWeekdayDate,
      },
      {
        field: 'start_time',
        type: 'date',
        headerName: t('donations.start-time'),
        width: 150,
        filterable: false,
        valueGetter: (_value, row) =>
          parseUTC(row.start_date, { timezone_id: row.donor_timezone_id }),
        valueFormatter: (value) =>
          formatDisplayDate(moment(value), 'timeWithSeconds'),
      },
      {
        field: 'title',
        type: 'string',
        headerName: t('donations.title'),
        width: 150,
      },
      {
        field: 'description',
        type: 'string',
        headerName: t('common.description'),
        width: 250,
        hide: true,
        renderCell: (params) => {
          const val = params.value || '';
          if (!val) return '';
          const truncated = val.length > 80;
          return (
            <Tooltip
              title={
                truncated ? (
                  <Box sx={{ whiteSpace: 'pre-wrap', maxWidth: 400, p: 0.5 }}>
                    {val}
                  </Box>
                ) : (
                  ''
                )
              }
              arrow
              placement="bottom-start"
            >
              <Typography
                variant="inherit"
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: truncated ? 'pointer' : 'default',
                }}
              >
                {val}
              </Typography>
            </Tooltip>
          );
        },
        csvValueGetter: (value) => (value || '').replace(/[\r\n]+/g, ' '),
      },
      {
        field: 'recurring_type',
        type: 'string',
        headerName: t('donations.recurring-interval'),
        width: 160,
        valueGetter: (_value) =>
          _value
            ? t(`donations.recurring-type.${_value}`)
            : t('donations.recurring-type.NOT-RECURRING'),
      },
      {
        field: 'pattern_start_date',
        type: 'date',
        headerName: t('donations.pattern-start-date'),
        width: 150,
        valueGetter: (_value, row) =>
          parseUTC(row.pattern_start_date, {
            timezone_id: row.donor_timezone_id,
          }),
        renderCell: renderWeekdayDate,
      },
      {
        field: 'pattern_end_date',
        type: 'date',
        headerName: t('donations.pattern-end-date'),
        width: 150,
        valueGetter: (_value, row) =>
          parseUTC(row.pattern_end_date, {
            timezone_id: row.donor_timezone_id,
          }),
        renderCell: renderWeekdayDate,
      },
      {
        field: 'donor_business_name',
        type: 'string',
        headerName: t('donations.donor'),
        width: 150,
      },
      {
        field: 'formatted_address',
        type: 'string',
        headerName: t('donations.address'),
        width: 200,
        filter: false,
      },
      {
        field: 'end_date',
        type: 'date',
        headerName: t('donations.end'),
        width: 150,
        valueGetter: (_value, row) =>
          parseUTC(row.end_date, { timezone_id: row.donor_timezone_id }),
        renderCell: renderWeekdayDate,
      },
      ...weightsAndTemps.map((col) => ({ ...col, filterable: false })),
    ],
    [i18n.language]
  );
};

// ----------------------------------------------------------------------

const IncomingRequestsContext = createContext();

export function IncomingRequestsProvider({ children }) {
  const [pageState, setPageState] = useState({ page: 0, pageSize: 25 });
  const columns = useIncomingRequestsColumns();

  const defaultDateRange = useMemo(
    () => ({
      start: null,
      end: null,
    }),
    []
  );
  const { DateRangeSelector, filterModel, handleSetFilterModel } =
    useDateFilterForTablePersist({
      defaultDateRange,
      // Normalizes date-column filter values to 'YYYY-MM-DD'.
      columns,
      pageId: 'incoming-requests',
    });

  const filterModelWithStatusFilter = useMemo(
    () => ({
      items: [
        ...(filterModel?.items ?? []),
        {
          id: 0,
          field: 'rescue_status',
          operator: 'equals',
          value: 'WAITING_FOR_APPROVAL',
        },
      ],
      logicOperator: 'and',
    }),
    [filterModel]
  );

  const { user } = useAuth();
  // Incoming requests is a team-user feature only. Admin/executive/corporate
  // roles have no incoming-requests UI (no sidebar badge, no page), so this
  // provider must not fire the (heavy) fetch for them.
  const incomingRequestsEnabled =
    user.account_type !== 'ADMIN' && !user.is_executive && !user.is_corporate;

  const { rescues, count, isLoading, invalidate } = useRescues({
    page: pageState.page,
    pageSize: pageState.pageSize,
    filterModel: filterModelWithStatusFilter,
    requests: true,
    includePatternDates: true,
    enabled: incomingRequestsEnabled,
  });

  useEventBus(
    'incoming-request',
    'invalidate',
    () => {
      invalidate();
    },
    [invalidate]
  );

  const value = {
    filterModel,
    setFilterModel: handleSetFilterModel,
    pageState,
    setPageState,
    rescues,
    count,
    isLoading,
    invalidate,
    DateRangeSelector,
  };

  return (
    <IncomingRequestsContext.Provider value={value}>
      {children}
    </IncomingRequestsContext.Provider>
  );
}

function useIncomingRequests() {
  const ctx = useContext(IncomingRequestsContext);

  if (!ctx) {
    throw new Error(
      'Could not use an IncomingRequestsContext. Did you forget an IncomingRequestsProvider'
    );
  }

  return ctx;
}

export function IncomingRequestsBadge({ location, children }) {
  const { count } = useIncomingRequests();

  if (location === 'center') {
    return (
      <MBadge
        badgeContent={count}
        color="error"
        sx={{ '& .MuiBadge-badge': { right: 0, top: 0 } }}
      />
    );
  }

  return (
    <MBadge badgeContent={count} color="error">
      {children}
    </MBadge>
  );
}

export default function IncomingRequestsTable() {
  const { t } = useTranslation('translation');
  const COLUMNS = useIncomingRequestsColumns();

  const {
    setFilterModel,
    pageState,
    setPageState,
    rescues,
    count,
    isLoading,
    invalidate,
  } = useIncomingRequests();

  const { user } = useAuth();

  return (
    <TableView
      getRowId={(row) => row.donation_participant_id || row.rescue_id || row.id}
      id="incomingdonations"
      title={t('sidebar.rescues.incoming-requests')}
      resolvePath={PATH_DASHBOARD.viewDonation}
      action={user.account_type === 'ADMIN' ? null : <CreateDonationButton />}
      rows={rescues}
      columns={COLUMNS}
      isLoading={isLoading}
      {...withPagination(pageState, setPageState, count, invalidate)}
      serverSideFiltering
      setFilterModel={setFilterModel}
    />
  );
}
